/* eslint-disable */
import React, { useState, useCallback, useContext, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import PricingTableFieldRow from './PricingTableFieldRow';
import PricingTableFieldHeader from './PricingTableFieldHeader';
import PricingTableSummary from './PricingTableSummary';
import DropdownMenu from './PricingDropdownMenu';
import FieldTitle from '../FieldTitle';
import Tooltip from '../../../misc/Tooltip';
import FieldModes from '../../../../constants/FieldModes';
import { filterColumnsByVisibility, getValueType, formatCellValue, calculateRowSubtotal } from './pricingHelpers';
import ThemeContext from '../../../../theme/ThemeContext';
import {
  handleCellChange,
  handleSummaryCellChange,
  handleMoveColumn,
  handleToggleColumnVisibility,
  handleInsertColumn,
  handleInsertSummaryColumn,
  handleChangeColumnType,
  handleChangeColumnFormat,
  handleChangeSummaryColumnFormat,
  handleDeleteColumn,
  handleDeleteSummaryColumn,
  handleInsertRowBelow,
  handleDeleteRow,
  showColumn,
  getHiddenColumnsMenuItems,
  updateSummaryCalculatedValues
} from './pricingHandlers';
import { PRICING_COLUMN_TYPES } from '../../../../constants/FieldPricingTableColumnTypes';

const TableContainer = styled.div`
  overflow-x: auto;
  width: 100%;
  position: relative;
  border-radius: ${(props) => {
    if (props.borderRadius) {
      return `${props.borderRadius}px`;
    } else if (props.theme.borderRadius) {
      return `${props.theme.borderRadius}px`;
    }
    return '0.5rem';
  }};
  overflow: hidden;
`;

const Table = styled.table`
  min-width: 98.9%;
  position: relative;
  border-collapse: separate;
  border-spacing: 0;
  background-color: ${(props) => props.backgroundColor || props.theme.backgroundColor || 'transparent'};
  
  th, td {
    text-align: left;
    vertical-align: middle;
    border: ${(props) => {
    if (props.borderWidth && props.borderColor) {
      return `${props.borderWidth}px solid ${props.borderColor}`;
    } else if (props.theme.borderWidth && props.theme.borderColor) {
      return `${props.theme.borderWidth}px solid ${props.theme.borderColor}`;
    }
    return '1px solid lightgrey';
  }};
    border-right-width: 0;
    border-bottom-width: 0;
    padding: ${(props) => {
    if (props.padding) {
      return `${props.padding}px`;
    } else if (props.theme.padding) {
      return `${props.theme.padding}px`;
    }
    return '0.5rem';
  }};
  }
  
  th:last-child, td:last-child {
    border-right-width: ${(props) => props.borderWidth || props.theme.borderWidth || 1}px;
  }
  
  tr:last-child td {
    border-bottom-width: ${(props) => props.borderWidth || props.theme.borderWidth || 1}px;
  }
  
  /* Apply border radius to specific corners */
  thead:first-child tr:first-child th:first-child {
    border-top-left-radius: ${(props) => {
    if (props.borderRadius) {
      return `${props.borderRadius}px`;
    } else if (props.theme.borderRadius) {
      return `${props.theme.borderRadius}px`;
    }
    return '0.5rem';
  }};
  }
  
  thead:first-child tr:first-child th:last-child {
    border-top-right-radius: ${(props) => {
    if (props.borderRadius) {
      return `${props.borderRadius}px`;
    } else if (props.theme.borderRadius) {
      return `${props.theme.borderRadius}px`;
    }
    return '0.5rem';
  }};
  }
  
  tbody:last-child tr:last-child td:first-child {
    border-bottom-left-radius: ${(props) => {
    if (props.borderRadius) {
      return `${props.borderRadius}px`;
    } else if (props.theme.borderRadius) {
      return `${props.theme.borderRadius}px`;
    }
    return '0.5rem';
  }};
  }
  
  tbody:last-child tr:last-child td:last-child {
    border-bottom-right-radius: ${(props) => {
    if (props.borderRadius) {
      return `${props.borderRadius}px`;
    } else if (props.theme.borderRadius) {
      return `${props.theme.borderRadius}px`;
    }
    return '0.5rem';
  }};
  }
`;

const PricingTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${(props) => {
    if (typeof props.margin === 'number') {
      return `margin: ${props.margin}px;`;
    }
    return '';
  }}
`;

const HiddenColumnsContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const HiddenColumnsButton = styled.button`
  background-color: ${(props) => props.theme.backgroundColor || '#f7fafc'};
  border: 1px solid ${(props) => props.theme.borderColor || '#e2e8f0'};
  border-radius: ${(props) => {
    if (props.theme.borderRadius) {
      return `${props.theme.borderRadius}px`;
    }
    return '0.25rem';
  }};
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: ${(props) => props.theme.fontColorPrimary || '#4a5568'};
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: ${(props) => props.theme.secondaryColor || '#edf2f7'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
  }
`;

const HiddenColumnBadge = styled.span`
  background-color: ${(props) => props.theme.borderColor || '#e2e8f0'};
  border-radius: 9999px;
  padding: 0.25rem 0.5rem;
  margin-left: 0.5rem;
  font-size: 0.75rem;
  color: ${(props) => props.theme.fontColorPrimary || '#4a5568'};
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right: 0;
  top: 0;
`;

const FieldTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const PricingTableField = ({
  value = { columns: [], summaryColumns: [] },
  onChange,
  onMeasureCompleted,

  disabled,
  mode,
  fieldMode,
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  title,
  tipVisible,
  tipTitle,
  tipDescription,
  margin,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //Column title styles
  columnTitleFontSize,
  columnTitleFontColor,
  columnTitleFontStyle,
  columnTitleFontWeight,
  columnTitleTextAlign,
  columnTitleTextTransform,
  columnTitleTextDecoration,
  columnTitleBackgroundColor,
  columnTitlePadding,

  //Styles
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderWidth,
  borderRadius,
  backgroundColor,
  padding,

  measure,
}) => {
  const theme = useContext(ThemeContext);
  const [wrapperRef, setWrapperRef] = useState(null);

  const [columns, setColumns] = useState(value.columns || []);
  const [summaryColumns, setSummaryColumns] = useState(value.summaryColumns || []);

  const formMode = mode;
  const currentMode = mode || fieldMode;
  const isEditable = currentMode !== FieldModes.readonly && !disabled;
  const canManageColumns = currentMode === FieldModes.edit && !disabled;
  const canEditQuantity = currentMode === FieldModes.fill && !disabled;

  let rowCount = 0;
  if (columns.length > 0) {
    rowCount = Math.max(...columns.map((col) => col.cells.length));
  }

  const handleChange = useCallback((updatedValue) => {
    if (onChange && !disabled) {
      onChange(fieldId, fieldIdentifier, fieldPositionId, updatedValue);
    }
  }, [onChange, fieldId, fieldIdentifier, fieldPositionId, disabled]);

  const onCellChange = useCallback((columnIndex, rowIndex, newValue) => {
    const updatedColumns = handleCellChange(columns, columnIndex, rowIndex, newValue);
    setColumns(updatedColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns
    });
  }, [columns, summaryColumns, handleChange]);

  const onSummaryCellChange = useCallback((columnIndex, newValue) => {
    if (disabled) {
      return;
    }

    const updatedSummaryColumns = handleSummaryCellChange(summaryColumns, columnIndex, newValue, columns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns,
      summaryColumns: updatedSummaryColumns
    });
  }, [summaryColumns, columns, disabled, handleChange]);

  const onMoveColumn = useCallback((columnIndex, direction) => {
    if (!canManageColumns) return;

    const updatedColumns = handleMoveColumn(columns, columnIndex, direction);
    setColumns(updatedColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns
    });
  }, [columns, summaryColumns, canManageColumns, handleChange]);

  const onToggleColumnVisibility = useCallback((columnIndex) => {
    if (!canManageColumns) return;

    const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } = handleToggleColumnVisibility(columns, columnIndex, summaryColumns);
    setColumns(updatedColumns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns: updatedSummaryColumns
    });
  }, [columns, summaryColumns, canManageColumns, handleChange]);

  const onShowColumn = useCallback((columnId) => {
    if (!canManageColumns) return;

    const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } = showColumn(columns, columnId, summaryColumns);
    setColumns(updatedColumns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns: updatedSummaryColumns
    });
  }, [columns, summaryColumns, canManageColumns, handleChange]);

  const onInsertColumn = useCallback((columnIndex, columnType) => {
    if (!canManageColumns) return;

    const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } = handleInsertColumn(columns, columnIndex, columnType, rowCount, summaryColumns);
    setColumns(updatedColumns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns: updatedSummaryColumns
    });
  }, [columns, rowCount, summaryColumns, canManageColumns, handleChange]);

  const onInsertSummaryColumn = useCallback((columnType, position = -1) => {
    if (!canManageColumns) return;

    const updatedSummaryColumns = handleInsertSummaryColumn(summaryColumns, columnType, position, columns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns,
      summaryColumns: updatedSummaryColumns
    });
  }, [summaryColumns, columns, canManageColumns, handleChange]);

  const onChangeColumnType = useCallback((columnIndex, newValueType) => {
    if (!canManageColumns) return;

    const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } = handleChangeColumnType(columns, columnIndex, newValueType, summaryColumns);
    setColumns(updatedColumns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns: updatedSummaryColumns
    });
  }, [columns, summaryColumns, canManageColumns, handleChange]);

  const onChangeColumnFormat = useCallback((columnIndex, newFormat) => {
    if (!canManageColumns) return;

    const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } = handleChangeColumnFormat(columns, columnIndex, newFormat, summaryColumns);
    setColumns(updatedColumns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns: updatedSummaryColumns
    });
  }, [columns, summaryColumns, canManageColumns, handleChange]);

  const onChangeSummaryColumnFormat = useCallback((columnId, newFormat) => {
    if (!canManageColumns) return;

    const updatedSummaryColumns = handleChangeSummaryColumnFormat(summaryColumns, columnId, newFormat, columns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns,
      summaryColumns: updatedSummaryColumns
    });
  }, [summaryColumns, columns, canManageColumns, handleChange]);

  const onDeleteColumn = useCallback((columnIndex) => {
    if (!canManageColumns) return;

    const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } = handleDeleteColumn(columns, columnIndex, summaryColumns);
    setColumns(updatedColumns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns: updatedSummaryColumns
    });
  }, [columns, summaryColumns, canManageColumns, handleChange]);

  const onDeleteSummaryColumn = useCallback((columnId) => {
    if (!canManageColumns) return;

    const updatedSummaryColumns = handleDeleteSummaryColumn(summaryColumns, columnId);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns,
      summaryColumns: updatedSummaryColumns
    });
  }, [summaryColumns, columns, canManageColumns, handleChange]);

  const onDeleteRow = useCallback((rowIndex) => {
    if (!isEditable) {
      return;
    }

    // Use the handleDeleteRow function from pricingHandlers
    const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } = handleDeleteRow(columns, rowIndex, summaryColumns);

    // Set columns with new reference
    setColumns(updatedColumns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns: updatedSummaryColumns
    });
  }, [columns, summaryColumns, isEditable, handleChange]);

  const onInsertRowBelow = useCallback((rowIndex) => {
    if (!isEditable) {
      return;
    }

    // Use the handleInsertRowBelow function from pricingHandlers
    const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } = handleInsertRowBelow(columns, rowIndex, summaryColumns);

    // Set columns with new reference
    setColumns(updatedColumns);
    setSummaryColumns(updatedSummaryColumns);

    handleChange({
      columns: updatedColumns,
      summaryColumns: updatedSummaryColumns
    });
  }, [columns, summaryColumns, isEditable, handleChange]);

  ////// Handlers End //////

  // Get visible and hidden columns
  const visibleColumns = filterColumnsByVisibility(columns, true);
  const hiddenColumns = filterColumnsByVisibility(columns, false);

  // Check if we have any visible columns
  const hasVisibleColumns = visibleColumns.length > 0;
  const hasHiddenColumns = hiddenColumns.length > 0;

  // Create menu items for the Hidden Columns dropdown
  const hiddenColumnsMenuItems = useCallback(() => {
    return getHiddenColumnsMenuItems(hiddenColumns, onShowColumn);
  }, [hiddenColumns, onShowColumn]);

  // Get tooltip component
  const getTooltip = () => {
    if (disabled) return null;

    return (
      <StyledTooltipWrapper>
        <Tooltip
          fieldId={fieldId}
          title={tipTitle}
          description={tipDescription}
          tipVisible={tipVisible}
        />
      </StyledTooltipWrapper>
    );
  };

  useEffect(() => {
    if (measure && onMeasureCompleted && wrapperRef && (columns.length > 0 || summaryColumns.length > 0)) {
      onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, wrapperRef.getBoundingClientRect());
    }
  }, [measure, onMeasureCompleted, fieldId, fieldIdentifier, fieldPositionId, wrapperRef, columns, summaryColumns]);

  // Ensure SUBTOTAL and TOTAL values are always calculated
  useEffect(() => {
    if (columns.length === 0 && summaryColumns.length === 0) return;

    let needsUpdate = false;
    const updatedColumns = [...columns];

    // Find all subtotal columns and update their cells with calculated values
    const subtotalColumnIndices = updatedColumns
      .map((col, index) => (col.type === PRICING_COLUMN_TYPES.SUBTOTAL ? index : -1))
      .filter((index) => index !== -1);

    if (subtotalColumnIndices.length > 0) {
      // For each row, calculate and update subtotal values
      const rowCount = updatedColumns[0]?.cells?.length || 0;
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        const subtotal = calculateRowSubtotal(updatedColumns, rowIndex);

        subtotalColumnIndices.forEach((columnIndex) => {
          const valueType = getValueType(updatedColumns[columnIndex]);
          const formattedSubtotal = formatCellValue(subtotal, valueType);

          // Only update if value has changed
          if (updatedColumns[columnIndex].cells[rowIndex].value !== formattedSubtotal) {
            updatedColumns[columnIndex].cells[rowIndex] = {
              ...updatedColumns[columnIndex].cells[rowIndex],
              value: formattedSubtotal
            };
            needsUpdate = true;
          }
        });
      }
    }

    // Update summary columns
    if (summaryColumns.length > 0) {
      const updatedSummaryColumns = updateSummaryCalculatedValues(updatedColumns, summaryColumns);

      // Check if any summary column values have changed
      const summaryValuesChanged = updatedSummaryColumns.some((col, i) => col.value !== summaryColumns[i].value);

      if (summaryValuesChanged) {
        setSummaryColumns(updatedSummaryColumns);
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      setColumns(updatedColumns);

      handleChange({
        columns: updatedColumns,
        summaryColumns: summaryColumns.length > 0
          ? updateSummaryCalculatedValues(updatedColumns, summaryColumns)
          : summaryColumns
      });
    }
  }, [columns, summaryColumns]);

  return (
    <PricingTableWrapper
      margin={margin}
      fieldMode={fieldMode}
      ref={(e) => {
        if (e && !wrapperRef) setWrapperRef(e);
      }}
    >
      <FieldTitleWrapper>
        <FieldTitle
          title={title}
          titleDisplay={titleDisplay}
          titleFontSize={titleFontSize}
          titleFontColor={titleFontColor}
          titleTextAlign={titleTextAlign}
          titleFontStyle={titleFontStyle}
          titleFontWeight={titleFontWeight}
          titleTextTransform={titleTextTransform}
          titleTextDecoration={titleTextDecoration}
        />
        {getTooltip()}
      </FieldTitleWrapper>

      <TableContainer
        borderRadius={borderRadius}
        theme={theme}
      >
        <Table
          backgroundColor={backgroundColor}
          borderRadius={borderRadius}
          borderColor={borderColor}
          borderWidth={borderWidth}
          padding={padding}
          theme={theme}
          width={(() => {
            if (isEditable) {
              return '98%';
            }
            return '98.9%';
          })()}
        >
          <PricingTableFieldHeader
            columns={columns}
            canManageColumns={canManageColumns}
            onMoveColumn={onMoveColumn}
            onInsertColumn={onInsertColumn}
            onChangeColumnType={onChangeColumnType}
            onChangeColumnFormat={onChangeColumnFormat}
            onDeleteColumn={onDeleteColumn}
            onToggleColumnVisibility={onToggleColumnVisibility}
            columnTitleFontSize={columnTitleFontSize}
            columnTitleFontColor={columnTitleFontColor}
            columnTitleFontStyle={columnTitleFontStyle}
            columnTitleFontWeight={columnTitleFontWeight}
            columnTitleTextAlign={columnTitleTextAlign}
            columnTitleTextTransform={columnTitleTextTransform}
            columnTitleTextDecoration={columnTitleTextDecoration}
            columnTitleBackgroundColor={columnTitleBackgroundColor}
            columnTitlePadding={columnTitlePadding}
            theme={theme}
          />
          <tbody>
            {hasVisibleColumns && [...Array(rowCount)].map((_, rowIndex) => (
              <PricingTableFieldRow
                key={`row-${rowIndex}`}
                rowIndex={rowIndex}
                columns={columns}
                onCellChange={onCellChange}
                onDeleteRow={onDeleteRow}
                onInsertRowBelow={onInsertRowBelow}
                mode={currentMode}
                formMode={formMode}
                canEditQuantity={canEditQuantity}
                isEditable={isEditable}
                canManageColumns={canManageColumns}
                fontSize={fontSize}
                fontColor={fontColor || theme.fontColorPrimary}
                fontStyle={fontStyle}
                fontWeight={fontWeight}
                textAlign={textAlign}
                textTransform={textTransform}
                textDecoration={textDecoration}
                textOverflow={textOverflow}
                backgroundColor={backgroundColor || theme.backgroundColor}
                borderColor={borderColor || theme.borderColor}
                borderWidth={borderWidth || theme.borderWidth}
                borderRadius={borderRadius || theme.borderRadius}
                padding={padding}
                theme={theme}
              />
            ))}
          </tbody>
        </Table>
      </TableContainer>

      {hasHiddenColumns && canManageColumns && (
        <HiddenColumnsContainer>
          <DropdownMenu
            triggerElement={(
              <HiddenColumnsButton theme={theme}>
                Hidden Columns
                <HiddenColumnBadge theme={theme}>{hiddenColumns.length}</HiddenColumnBadge>
              </HiddenColumnsButton>
            )}
            menuItems={hiddenColumnsMenuItems()}
          />
        </HiddenColumnsContainer>
      )}

      <PricingTableSummary
        columns={columns}
        summaryColumns={summaryColumns}
        onSummaryCellChange={onSummaryCellChange}
        onChangeSummaryColumnFormat={onChangeSummaryColumnFormat}
        onDeleteSummaryColumn={onDeleteSummaryColumn}
        onInsertSummaryColumn={onInsertSummaryColumn}
        canManageColumns={canManageColumns}
        mode={currentMode}
        formMode={formMode}
        fontSize={fontSize}
        fontColor={fontColor || theme.fontColorPrimary}
        fontStyle={fontStyle}
        fontWeight={fontWeight}
        textAlign={textAlign}
        textTransform={textTransform}
        textDecoration={textDecoration}
        textOverflow={textOverflow}
        backgroundColor={backgroundColor || theme.backgroundColor}
        borderColor={borderColor || theme.borderColor}
        borderWidth={borderWidth || theme.borderWidth}
        borderRadius={borderRadius || theme.borderRadius}
        padding={padding}
        theme={theme}
      />
    </PricingTableWrapper>
  );
};

export default PricingTableField;

PricingTableField.propTypes = {
  value: PropTypes.shape({
    columns: PropTypes.array,
    summaryColumns: PropTypes.array
  }),
  onChange: PropTypes.func,
  onMeasureCompleted: PropTypes.func,
  disabled: PropTypes.bool,
  mode: PropTypes.string,
  fieldMode: PropTypes.string,
  fieldId: PropTypes.string,
  fieldIdentifier: PropTypes.string,
  fieldPositionId: PropTypes.string,
  title: PropTypes.string,
  tipVisible: PropTypes.bool,
  tipTitle: PropTypes.string,
  tipDescription: PropTypes.string,
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  // Title styles
  titleDisplay: PropTypes.string,
  titleFontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  titleFontColor: PropTypes.string,
  titleTextAlign: PropTypes.string,
  titleFontStyle: PropTypes.string,
  titleFontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  titleTextTransform: PropTypes.string,
  titleTextDecoration: PropTypes.string,

  // Column title styles
  columnTitleFontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  columnTitleFontColor: PropTypes.string,
  columnTitleFontStyle: PropTypes.string,
  columnTitleFontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  columnTitleTextAlign: PropTypes.string,
  columnTitleTextTransform: PropTypes.string,
  columnTitleTextDecoration: PropTypes.string,
  columnTitleBackgroundColor: PropTypes.string,
  columnTitlePadding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  // Styles
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontColor: PropTypes.string,
  fontStyle: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  textAlign: PropTypes.string,
  textTransform: PropTypes.string,
  textDecoration: PropTypes.string,
  textOverflow: PropTypes.string,
  borderColor: PropTypes.string,
  borderWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  backgroundColor: PropTypes.string,
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  measure: PropTypes.bool,
};

